import { useState } from "react";
import Reveal from "./Reveal";
import PauseButton from "./PauseButton";
import Btn from "./Btn";
import { REGISTRATION_WEEK } from "../data/launch";
import "./AnnouncementBanner.css";

/** Home-page announcements — the always-there ticker (marketing's replacement for the
 *  old pop-up) followed by a centered 2027 registration-week callout. Add / edit /
 *  remove ticker lines in `announcements` and it adapts. Brand red dots between items. */
const announcements = [
  `2027 Registration Week: ${REGISTRATION_WEEK}`,
  "2027 Trip Leader Applications Open Now!",
];

const Group = ({ hidden = false }: { hidden?: boolean }) => {
  // Repeat so even a couple of short lines fill a wide viewport.
  const repeated = Array.from({ length: 4 }).flatMap(() => announcements);
  return (
    <ul className="ann-ticker__group" aria-hidden={hidden ? "true" : undefined}>
      {repeated.map((text, i) => (
        <li key={i} className="ann-ticker__item">
          <span className="ann-ticker__dot" aria-hidden="true" />
          <span className="btn-caps text-sm tracking-[0.08em] text-foreground/85 md:text-base">{text}</span>
        </li>
      ))}
    </ul>
  );
};

const AnnouncementBanner = () => {
  const [paused, setPaused] = useState(false);
  return (
    <>
      {/* Full-width scrolling ticker */}
      <section
        aria-label="HXP announcements"
        className="ann-ticker relative border-t border-hxp-sand-dark bg-hxp-cream"
      >
        <div className="ann-ticker__viewport group relative overflow-hidden">
          {/* two identical groups → seamless -50% loop; hidden from AT */}
          <div className={`ann-ticker__track${paused ? " is-paused" : ""}`} aria-hidden="true">
            <Group />
            <Group hidden />
          </div>
          {/* static, screen-reader-only copy of the real announcements */}
          <ul className="sr-only">
            {announcements.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </div>
        {/* hover pauses it for mice; this pauses it for keyboards and phones (WCAG 2.2.2) */}
        <PauseButton
          paused={paused}
          onToggle={() => setPaused((v) => !v)}
          what="announcements"
          className="absolute right-3 top-1/2 z-10 -translate-y-1/2 motion-reduce:hidden"
        />
      </section>

      {/* Centered registration-week callout */}
      <section className="border-y border-hxp-sand-dark bg-hxp-cream">
        <Reveal>
          <div className="mx-auto max-w-[1000px] px-6 py-14 text-center md:py-20">
            <p className="btn-caps text-sm tracking-[0.2em] text-primary">Save the date · 2027</p>
            <h2 className="mt-3 text-5xl md:text-7xl">
              Registration <span className="text-primary">Week</span>
            </h2>
            <p className="font-heading mt-4 text-4xl leading-tight text-foreground md:text-6xl">
              November&nbsp;30 – December&nbsp;4
            </p>
            <p className="accent-hand mx-auto mt-5 text-lg text-foreground/70 md:text-xl">
              Mark your calendar. Be ready.
            </p>
            <div className="mt-8">
              <Btn href="/registration/">How registration works</Btn>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default AnnouncementBanner;
